import { API } from '@/modules/api'
import type { TMbaCafeLunchCartLine } from '@/types/cart'
import { defineStore } from 'pinia'
import { useCartStore } from '@/stores/cart'
import { OrderItemType, OrderItemTypeCode } from '@/types/orderItemType'

export const createUniqueId = (feeId: string) => feeId + '::' + OrderItemTypeCode.MbaCafeLunch

export const useMbaCafeLunchStore = defineStore('mbaCafeLunch', {
  state: () => ({
    isLoaded: {} as Record<TStudentAccountId, boolean>,
    mbaCafeBalances: {} as Record<TStudentAccountId, TMbaCafeLunchCartLine[]>
  }),

  getters: {
    isLoading(state) {
      return (studentAccountId: TStudentAccountId) => !(state.isLoaded[studentAccountId] ?? false)
    },
    getMbaCafeBalance(state) {
      return (studentAccountId: TStudentAccountId) => state.mbaCafeBalances[studentAccountId] ?? []
    }
  },

  actions: {
    async fetch(districtId: TDistrictId, studentAccountId: TStudentAccountId) {
      this.isLoaded[studentAccountId] = false
      const mbaCafeBalance = await API.Get.mbaCafeLunch(studentAccountId)
      this.mbaCafeBalances[studentAccountId] = mbaCafeBalance
      this.validateCart(districtId, studentAccountId)
      this.isLoaded[studentAccountId] = true
    },

    // This function is used to validate the cart lines for the mba cafe lunch store
    // It checks if the cart lines are valid compared to the data from the API, and if not, it removes them from the cart.
    // This prevents the cart from attempting to get order line estimates or checking out with invalid data.
    validateCart(districtId: TDistrictId, studentAccountId: TStudentAccountId) {
      const cartStore = useCartStore()

      for (const line of cartStore.getCartLines(districtId) as TMbaCafeLunchCartLine[]) {
        if (
          line.orderItemType === OrderItemType.MbaCafeLunch &&
          line.studentAccountId === studentAccountId
        ) {
          const balance = this.mbaCafeBalances[studentAccountId].find(
            balance => balance.feeId === line.feeId
          )
          if (!balance) {
            console.log('balance not found, removing from cart', line.feeId)
            this.removeFromCart(districtId, line.feeId)
          }
        }
      }
    },

    addToCart(districtId: TDistrictId, feeData: Omit<TMbaCafeLunchCartLine, 'uniqueId'>) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(feeData.feeId)
      const existingLine = cartStore.getCartLine(districtId, uniqueId)

      if (existingLine) {
        cartStore.updateCartLine(districtId, uniqueId, feeData)
      } else {
        cartStore.addCartLine(districtId, uniqueId, { ...feeData, uniqueId })
      }
    },

    updateCartLine(
      districtId: TDistrictId,
      feeData: Partial<TMbaCafeLunchCartLine> & { feeId: string }
    ) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(feeData.feeId)
      cartStore.updateCartLine(districtId, uniqueId, feeData)
    },

    removeFromCart(districtId: TDistrictId, feeId: string) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(feeId)
      cartStore.removeCartLine(districtId, uniqueId)
    },

    clearCartForDistrict(districtId: TDistrictId) {
      const cartStore = useCartStore()
      for (const line of cartStore.getCartLines(districtId)) {
        if (line.orderItemType === OrderItemType.MbaCafeLunch) {
          this.removeFromCart(districtId, (line as TMbaCafeLunchCartLine).feeId)
        }
      }
    }
  }
})
